import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, ArrowLeft, Play, X, Image as ImageIcon, Film } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LocationCards() {
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    axios.get(`${API}/library/locations`).then(res => setLocations(res.data)).catch(console.error);
  }, []);

  return (
    <div className="pt-20" data-testid="gallery-page">
      <section className="bg-[#222] text-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Gallery</h1>
          <p className="text-base sm:text-lg text-white/80 max-w-2xl">
            Explore our premium workspaces across Bangalore through stunning imagery and videos.
          </p>
        </div>
      </section>

      <section className="section-padding bg-[#F5F5F5]">
        <div className="container mx-auto px-4">
          {locations.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-[#666] text-lg">Gallery coming soon.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {locations.map((loc) => (
                <Link
                  key={loc.id}
                  to={`/gallery/${loc.slug}`}
                  className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all hover:-translate-y-1"
                  data-testid={`gallery-location-${loc.slug}`}
                >
                  <div className="relative h-56 bg-[#333] overflow-hidden">
                    {loc.cover_image ? (
                      <img
                        src={loc.cover_image}
                        alt={loc.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon size={48} className="text-white/30" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <h2 className="text-xl font-bold text-white mb-1">{loc.name}</h2>
                      {loc.description && (
                        <p className="text-white/80 text-sm line-clamp-2">{loc.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-[#666]">
                      <MapPin size={16} />
                      <span className="text-sm">{loc.name}</span>
                    </div>
                    <span className="text-sm font-semibold text-[#E12429]">
                      {loc.media_count} {loc.media_count === 1 ? 'item' : 'items'}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function LocationGallery() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [location, setLocation] = useState(null);
  const [filter, setFilter] = useState('all');
  const [lightbox, setLightbox] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/library/locations/${slug}`)
      .then(res => { setLocation(res.data); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, [slug]);

  if (loading) return <div className="pt-20 min-h-screen flex items-center justify-center"><p>Loading...</p></div>;
  if (!location) return (
    <div className="pt-20 min-h-screen flex items-center justify-center">
      <div className="text-center">
        <p className="text-xl text-[#666] mb-4">Location not found</p>
        <button onClick={() => navigate('/gallery')} className="btn-primary">Back to Gallery</button>
      </div>
    </div>
  );

  const media = location.media || [];
  const images = media.filter(m => m.media_type === 'image');
  const videos = media.filter(m => m.media_type === 'video');
  const filtered = filter === 'all' ? media : filter === 'images' ? images : videos;

  const isVideo = (url) => {
    if (!url) return false;
    return url.match(/\.(mp4|webm|ogg|mov)$/i) || url.includes('youtube.com') || url.includes('youtu.be') || url.includes('vimeo.com');
  };

  const getYoutubeEmbedUrl = (url) => {
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  return (
    <div className="pt-20" data-testid="gallery-detail-page">
      <section className="bg-[#222] text-white py-12">
        <div className="container mx-auto px-4">
          <button
            onClick={() => navigate('/gallery')}
            className="flex items-center gap-2 text-white/70 hover:text-white mb-4 transition-colors"
            data-testid="back-to-gallery"
          >
            <ArrowLeft size={18} />
            <span>Back to Gallery</span>
          </button>
          <h1 className="text-4xl sm:text-5xl font-bold mb-2">{location.name}</h1>
          {location.description && <p className="text-white/80 text-lg">{location.description}</p>}
        </div>
      </section>

      <section className="bg-white py-4 border-b sticky top-[72px] z-10">
        <div className="container mx-auto px-4 flex items-center gap-3">
          {[
            { key: 'all', label: `All (${media.length})` },
            { key: 'images', label: `Images (${images.length})` },
            { key: 'videos', label: `Videos (${videos.length})` },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                filter === tab.key ? 'bg-[#E12429] text-white' : 'bg-[#F5F5F5] text-[#666] hover:bg-[#eee]'
              }`}
              data-testid={`filter-${tab.key}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </section>

      <section className="section-padding bg-[#F5F5F5]">
        <div className="container mx-auto px-4">
          {filtered.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-[#666] text-lg">No media found.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => setLightbox(item)}
                  className="relative h-64 rounded-lg overflow-hidden group cursor-pointer bg-[#333]"
                  data-testid={`media-item-${index}`}
                >
                  {item.media_type === 'video' ? (
                    <>
                      {getYoutubeEmbedUrl(item.url) ? (
                        <img
                          src={`https://img.youtube.com/vi/${item.url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/)?.[1]}/hqdefault.jpg`}
                          alt={item.caption || 'Video'}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <video src={item.url} className="w-full h-full object-cover" muted />
                      )}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-14 h-14 rounded-full bg-[#E12429] flex items-center justify-center shadow-lg">
                          <Play size={24} className="text-white ml-1" />
                        </div>
                      </div>
                    </>
                  ) : (
                    <img
                      src={item.url}
                      alt={item.caption || 'Gallery image'}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all" />
                  {item.caption && (
                    <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70 to-transparent">
                      <p className="text-white text-sm">{item.caption}</p>
                    </div>
                  )}
                  <div className="absolute top-3 right-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      item.media_type === 'video' ? 'bg-[#E12429] text-white' : 'bg-white/90 text-[#333]'
                    }`}>
                      {item.media_type === 'video' ? <Film size={12} className="inline mr-1" /> : <ImageIcon size={12} className="inline mr-1" />}
                      {item.media_type}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {lightbox && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="lightbox-overlay">
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-200 z-10" data-testid="close-lightbox">
            <X size={20} />
          </button>
          <div className="max-w-5xl w-full max-h-[90vh]" onClick={e => e.stopPropagation()}>
            {lightbox.media_type === 'video' ? (
              getYoutubeEmbedUrl(lightbox.url) ? (
                <iframe
                  src={`${getYoutubeEmbedUrl(lightbox.url)}?autoplay=1`}
                  className="w-full aspect-video rounded-lg"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  title={lightbox.caption || 'Video'}
                />
              ) : (
                <video src={lightbox.url} controls autoPlay className="w-full max-h-[80vh] rounded-lg" />
              )
            ) : (
              <img src={lightbox.url} alt={lightbox.caption || ''} className="max-w-full max-h-[85vh] object-contain rounded-lg mx-auto" />
            )}
            {lightbox.caption && <p className="text-white text-center mt-4 text-lg">{lightbox.caption}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Gallery() {
  return null; // Wrapper - not used directly
}

export { LocationCards, LocationGallery };
